import * as React from 'react'
import axios from 'axios'
import { usePaywallStore } from './usePaywallStore'
import {
    failureCode,
    failureMessage,
    paymentOutcomeUnknown,
    refusalMessage,
} from './checkoutErrors'

// A first purchase, by card or wallet. Whatever authorised the intent, only
// the server turns it into a subscription (/billing/checkout/complete).
export function usePurchase({
    selectedPlan,
    details,
    period,
    config,
    card,
    intents,
    funnel,
    onDone,
    onVatRefused,
}) {
    const completeUpgrade = usePaywallStore(state => state.completeUpgrade)
    const [status, setStatus] = React.useState('idle')
    const [error, setError] = React.useState(null)
    const [attempts, setAttempts] = React.useState(0)
    // A completion that failed after authorisation may already have charged:
    // the next click finishes that same payment, never a new one.
    const [pendingCompletion, setPendingCompletion] = React.useState(null)

    // What the buyer is paying for, as on screen when they paid: completion
    // brings the Chargebee customer in line with these billing details.
    function currentPurchase() {
        return {
            plan: selectedPlan,
            coupon: details.coupon,
            country: details.country,
            company: details.company,
            vatNumber: details.vatNumber,
        }
    }

    async function completeWithIntent(paymentIntentId, method, purchase) {
        const response = await axios.post('/billing/checkout/complete', {
            plan: purchase.plan.planId,
            paymentIntentId,
            coupon: purchase.coupon || undefined,
            country: purchase.country || undefined,
            company: purchase.company || undefined,
            vatNumber: purchase.vatNumber || undefined,
        })
        completeUpgrade({
            plan: response.data.plan,
            tier: response.data.tier,
            method,
            amount: response.data.amount,
            currency: response.data.currency,
            fbEventId: response.data.fbEventId,
            direction: 'new',
            code: funnel.funnelCode,
            period,
            testSite: Boolean(config && config.testSite),
        })
        setStatus('idle')
        onDone({
            label: purchase.plan.label,
            mode: 'buy',
            amount: response.data.amount,
            currency: response.data.currency,
        })
    }

    function payFailed(
        payError,
        method = 'card',
        message = null,
        refusedIntentId = null
    ) {
        const failure = {
            method,
            error_code: failureCode(payError),
            error_message: failureMessage(payError),
            attempt: attempts + 1,
        }
        // The gateway's reason is on the refused intent, which only the server
        // can read; the buyer's message does not wait for it.
        const declined = refusedIntentId
            ? axios
                  .post('/billing/checkout/payment-failure', {
                      paymentIntentId: refusedIntentId,
                  })
                  .then(response => response.data)
                  .catch(() => null)
            : Promise.resolve(null)
        declined.then(decline =>
            funnel.track('upgrade_checkout_failed', {
                ...failure,
                decline_code: decline && decline.declineCode,
                gateway_error_code: decline && decline.errorCode,
            })
        )
        if (method === 'card') intents.discardCardIntent()
        // Nothing was charged: the refusal belongs under the VAT field, and
        // the order falls back to VAT for the buyer to fix or pay.
        if (failureCode(payError) === 'VAT_INVALID') {
            onVatRefused()
            setStatus('idle')
            setError(null)
            return
        }
        setAttempts(count => count + 1)
        setStatus('failed')
        setError(
            message ||
                refusalMessage(
                    payError,
                    'The payment did not go through. Your card was not charged.'
                )
        )
    }

    async function finishPurchase(paymentIntentId, method, purchase) {
        try {
            await completeWithIntent(paymentIntentId, method, purchase)
            setPendingCompletion(null)
        } catch (completeError) {
            if (!paymentOutcomeUnknown(completeError)) {
                setPendingCompletion(null)
                if (method !== 'card') intents.renew()
                payFailed(completeError, method)
                return
            }
            setPendingCompletion({ paymentIntentId, method, ...purchase })
            payFailed(
                completeError,
                method,
                completeError.response?.data?.code === 'PLAN_NOT_SAVED'
                    ? `Your payment for ${purchase.plan.label} went through, but we could not switch on your plan yet. Press “Finish upgrade” to try again. You will not be charged twice.`
                    : `We could not confirm your payment for ${purchase.plan.label}. Press “Finish upgrade” to check again. You will not be charged twice.`
            )
        }
    }

    async function payByCard() {
        setStatus('processing')
        setError(null)
        if (pendingCompletion) {
            const { paymentIntentId, method, ...purchase } = pendingCompletion
            await finishPurchase(paymentIntentId, method, purchase)
            return
        }
        funnel.track('upgrade_checkout_started', { method: 'card' })
        const purchase = currentPurchase()
        let checkout = null
        let authorized
        try {
            checkout = await intents.requestIntent()
            // Card data never leaves Chargebee's iframes; 3DS runs inside
            // authorizeWith3ds when the gateway asks for it.
            authorized = await card.authorize(checkout.paymentIntent, {
                country: details.country,
            })
        } catch (payError) {
            payFailed(
                payError,
                'card',
                null,
                checkout && checkout.paymentIntent && checkout.paymentIntent.id
            )
            return
        }
        await finishPurchase(authorized.id, 'card', purchase)
    }

    function walletStarted(method) {
        funnel.track('upgrade_checkout_started', { method })
        setStatus('processing')
        setError(null)
    }

    // Without it a closed wallet sheet and a button that silently broke look the
    // same: a start with no outcome.
    function walletCancelled(method) {
        funnel.track('upgrade_checkout_cancelled', { method })
        setStatus('idle')
    }

    async function payWithWallet(authorizedIntent, method) {
        setStatus('processing')
        await finishPurchase(authorizedIntent.id, method, currentPurchase())
    }

    return {
        status,
        error,
        attempts,
        pendingCompletion,
        payByCard,
        payWithWallet,
        walletStarted,
        walletCancelled,
        payFailed,
    }
}
